import { Order, OrderItem } from '@/models/sales/Order'
import { CustomerInvoice } from '@/models/sales/CustomerInvoice'
import { v4 as uuidv4 } from 'uuid'
import Item from '@/models/inventory/Item'
import Customer from '@/models/customer/Customer'

interface CheckoutTab {
  id: string
  title: string
  name: number
  content: string
  order: Order
  hasChanges: boolean
}

/**
 * Creates a new checkout tab with pre-populated order items
 * @param items - Array of items to add to the order
 * @param customer - Optional customer to associate with the order
 * @returns The created tab object
 */
export function createCheckoutTab(items: Item[], customer?: Customer | null): CheckoutTab {
  // Create new order with items
  const newOrder = new Order()
  newOrder.currency = 'IQD'

  // Add customer to order and invoice if available
  if (customer) {
    newOrder.customerID = customer.id || ''

    // Create invoice with customer
    newOrder.invoice = new CustomerInvoice()
    newOrder.invoice.customerID = customer.id || ''
    newOrder.invoice.customer = customer
  }

  // Add items to order
  items.forEach(item => {
    const orderItem = new OrderItem(item)
    orderItem.quantity = 1
    newOrder.orderItems.push(orderItem)
  })

  // Get existing checkout tabs from localStorage
  const checkoutTabsJSON = localStorage.getItem('checkoutTabs')
  const checkoutTabIndexJSON = localStorage.getItem('checkoutTabIndex')

  let checkoutTabs: CheckoutTab[] = checkoutTabsJSON ? JSON.parse(checkoutTabsJSON) : []
  let tabIndex = checkoutTabIndexJSON ? parseInt(checkoutTabIndexJSON) : 1

  // Create new tab
  const newTabIndex = ++tabIndex
  const newTab: CheckoutTab = {
    id: uuidv4(),
    title: `Sale #${tabIndex}`,
    name: newTabIndex,
    content: '',
    order: newOrder,
    hasChanges: false
  }

  // Add new tab
  checkoutTabs.push(newTab)

  // Save to localStorage
  localStorage.setItem('checkoutTabs', JSON.stringify(checkoutTabs))
  localStorage.setItem('checkoutTabIndex', String(tabIndex))
  localStorage.setItem('checkoutTabValue', String(newTabIndex))

  return newTab
}

/**
 * Clears all checkout tabs from localStorage
 */
export function clearCheckoutTabs(): void {
  localStorage.removeItem('checkoutTabs')
  localStorage.removeItem('checkoutTabIndex')
  localStorage.removeItem('checkoutTabValue')
}

/**
 * Gets all checkout tabs from localStorage
 */
export function getCheckoutTabs(): CheckoutTab[] {
  const checkoutTabsJSON = localStorage.getItem('checkoutTabs')
  return checkoutTabsJSON ? JSON.parse(checkoutTabsJSON) : []
}
